import { isFuture, isPast, isValid, parse as parseDate } from 'date-fns';
import { PhoneNumberType, PhoneNumberUtil } from 'google-libphonenumber';
import * as Yup from 'yup';

import { VALIDATION_CONSTANTS } from 'global/constants';

export const YDateRequired = Yup.string()
  .required(VALIDATION_CONSTANTS.MANDATORY)
  .transform((value, originalValue) => {
    if (!originalValue) {
      return originalValue;
    }
    // Normalize comma to dot for dd.MM.yyyy format
    const normalized = String(originalValue).replace(/,/g, '.');
    if (normalized.length !== 10) {
      return false;
    }

    const result = parseDate(normalized, 'dd.MM.yyyy', new Date());

    if (!isValid(result)) {
      return false;
    }
    return result.toDateString();
  })
  .typeError('Pflichtformat: dd.MM.yyyy');

export const YDate = Yup.string()
  .transform((value, originalValue) => {
    if (!originalValue || String(originalValue).trim().length === 0) {
      return originalValue;
    }
    // Normalize comma to dot for dd.MM.yyyy format
    const normalized = String(originalValue).replace(/,/g, '.');
    if (normalized.length !== 10) {
      return false;
    }

    const result = parseDate(normalized, 'dd.MM.yyyy', new Date());

    if (!isValid(result)) {
      return false;
    }
    return result.toDateString();
  })
  .typeError('Pflichtformat: dd.MM.yyyy');

export const YFutureDate = Yup.string()
  .transform((value, originalValue) => {
    if (!originalValue) {
      return originalValue;
    }
    // Normalize comma to dot for dd.MM.yyyy format
    const normalized = String(originalValue).replace(/,/g, '.');
    if (normalized.length !== 10) {
      return null;
    }

    const result = parseDate(normalized, 'dd.MM.yyyy', new Date());

    if (!isValid(result) || !isFuture(result)) {
      return false;
    }
    return result.toDateString();
  })
  .typeError('Pflichtformat: dd.MM.yyyy & Datum in der Zukunft wählen');

export const YPastDate = Yup.string()
  .transform((value, originalValue) => {
    if (!originalValue) {
      return originalValue;
    }
    // Normalize comma to dot for dd.MM.yyyy format
    const normalized = String(originalValue).replace(/,/g, '.');
    if (normalized.length !== 10) {
      return null;
    }

    const result = parseDate(normalized, 'dd.MM.yyyy', new Date());

    if (!isValid(result) || !isPast(result)) {
      return false;
    }
    return result.toDateString();
  })
  .typeError('Pflichtformat: dd.MM.yyyy & Datum in der Vergangenheit wählen');

// TODO: YZIP refactor as it's not working with numbers, for now string are fine
export const YZipRequired = Yup.string()
  .required(VALIDATION_CONSTANTS.MANDATORY)
  .transform((value, originalValue) => {
    if (!originalValue) {
      return originalValue;
    }
    if (originalValue.length === 0) {
      return value;
    }

    if (originalValue.length !== 5) {
      return null;
    }

    const pattern = /^\s*?\d{5}(?:[-\s]\d{4})?\s*?$/;

    const result = pattern.test(String(originalValue));

    if (result) {
      return originalValue;
    }
    return null;
  })
  .typeError(VALIDATION_CONSTANTS.INVALID_POSTAL);

export const YZip = Yup.string()
  .transform((value, originalValue) => {
    if (originalValue.length === 0) {
      return value;
    }

    if (originalValue.length !== 5) {
      return null;
    }

    const pattern = /^\s*?\d{5}(?:[-\s]\d{4})?\s*?$/;

    const result = pattern.test(String(originalValue));

    if (result) {
      return originalValue;
    }
    return null;
  })
  .typeError(VALIDATION_CONSTANTS.INVALID_POSTAL);

export const YPhoneNumber = Yup.string().test(
  'is-phone-number',
  VALIDATION_CONSTANTS.INVALID_PHONE,
  (value) => {
    if (!value || String(value).trim().length === 0) {
      return true;
    }

    const valueForProcessing = String(value);

    if (/[a-zA-Z]/.test(valueForProcessing)) {
      return false;
    }

    const phoneUtil = PhoneNumberUtil.getInstance();
    const DEFAULT_REGION = 'DE';
    const getPhoneNumber = () => {
      try {
        return phoneUtil.parse(valueForProcessing, DEFAULT_REGION);
      } catch (error) {
        return null;
      }
    };

    const phoneNumber = getPhoneNumber();
    if (!phoneNumber) {
      return false;
    }

    // If the user visually separated a leading group after the "+" (e.g. "+499 84887"),
    // that group must be the actual country code. Otherwise the input claims a
    // non-existent country code even if the digits normalize to a valid number.
    const leadingGroupMatch = valueForProcessing.trim().match(/^\+(\d+)[\s\-./()]/);
    if (leadingGroupMatch && leadingGroupMatch[1] !== String(phoneNumber.getCountryCode())) {
      return false;
    }

    // Use region-aware validation to match EIV behavior
    const regionCode = phoneUtil.getRegionCodeForNumber(phoneNumber);
    if (!regionCode) return false;

    if (!phoneUtil.isValidNumberForRegion(phoneNumber, regionCode)) {
      return false;
    }

    const numberTypeEnum = phoneUtil.getNumberType(phoneNumber);

    const isValidCountry = ['DE', 'AT', 'CH', 'LU', 'BE', 'IT', 'SI', 'PL', 'DK'].includes(
      regionCode
    );

    const getNumberType = () => {
      if (numberTypeEnum === PhoneNumberType.MOBILE) return 'MOBILE';
      if (numberTypeEnum === PhoneNumberType.FIXED_LINE) return 'FIXED_LINE';
      if (numberTypeEnum === PhoneNumberType.FIXED_LINE_OR_MOBILE) return 'FIXED_LINE_OR_MOBILE';
      return 'UNKNOWN';
    };
    const typeString = getNumberType();

    const isValidType = ['MOBILE', 'FIXED_LINE', 'FIXED_LINE_OR_MOBILE'].includes(typeString);

    const result = isValidCountry && isValidType;

    return result;
  }
);
